import { getCard } from "../../content/lookups.js";
import { CARD_ART } from "../../components/CardView.js";
import type { CardInstance } from "../../domain/types/index.js";

type ClearImageInput = {
  stageName: string;
  finalCaseName: string;
  fatigue: number;
  clearedNodes: number;
  organizations: string[];
  deck: CardInstance[];
};

const WIDTH = 1080;
const CARD_WIDTH = 280;
const CARD_HEIGHT = 373;
const CARD_X = [80, 400, 720];

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.closePath();
}

function drawCover(ctx: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, width: number, height: number) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  ctx.drawImage(
    image,
    (image.naturalWidth - sourceWidth) / 2,
    (image.naturalHeight - sourceHeight) / 2,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height,
  );
}

async function loadArt(cardId: string): Promise<HTMLImageElement | null> {
  const source = CARD_ART[cardId];
  if (!source) return null;
  const image = new Image();
  image.src = source;
  try {
    await image.decode();
    return image;
  } catch {
    return null;
  }
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  card: CardInstance,
  image: HTMLImageElement | null,
  x: number,
  y: number,
) {
  const definition = getCard(card.cardId);
  const isWorker = definition?.type === "worker";
  const accent = isWorker ? "#2db89a" : "#d4a017";
  const stock = isWorker ? "#e1eee5" : "#f2e3c4";

  ctx.save();
  roundedRect(ctx, x, y, CARD_WIDTH, CARD_HEIGHT, 18);
  ctx.fillStyle = stock;
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = accent;
  ctx.stroke();
  ctx.clip();

  if (image) drawCover(ctx, image, x + 4, y + 48, CARD_WIDTH - 8, CARD_HEIGHT - 142);
  else {
    ctx.fillStyle = isWorker ? "#c7dfd1" : "#ead4a5";
    ctx.fillRect(x + 4, y + 48, CARD_WIDTH - 8, CARD_HEIGHT - 142);
  }

  ctx.fillStyle = "rgba(250, 247, 239, .94)";
  ctx.fillRect(x + 4, y + 4, CARD_WIDTH - 8, 47);
  ctx.fillStyle = "#242820";
  ctx.font = '700 24px "IBM Plex Sans JP", sans-serif';
  ctx.fillText(definition?.name ?? card.cardId, x + 16, y + 34, CARD_WIDTH - 32);

  ctx.fillStyle = accent;
  roundedRect(ctx, x + CARD_WIDTH - 91, y + 62, 74, 28, 14);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = '700 14px "IBM Plex Sans JP", sans-serif';
  ctx.textAlign = "center";
  ctx.fillText(isWorker ? "労働力" : "施策", x + CARD_WIDTH - 54, y + 82);

  ctx.fillStyle = stock;
  ctx.fillRect(x + 4, y + CARD_HEIGHT - 94, CARD_WIDTH - 8, 90);
  ctx.fillStyle = accent;
  ctx.fillRect(x + 4, y + CARD_HEIGHT - 94, CARD_WIDTH - 8, 5);
  ctx.fillStyle = "#314038";
  ctx.font = '700 17px "IBM Plex Sans JP", sans-serif';
  ctx.textAlign = "center";
  ctx.fillText(definition?.text ?? "", x + CARD_WIDTH / 2, y + CARD_HEIGHT - 43, CARD_WIDTH - 28);
  ctx.restore();
}

function download(canvas: HTMLCanvasElement, stageName: string) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${stageName}-clear-result.png`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, "image/png");
}

export async function exportClearImage(input: ClearImageInput) {
  const rows = Math.max(1, Math.ceil(input.deck.length / 3));
  const height = 520 + rows * 420 + 148;
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const artwork = new Map(await Promise.all(input.deck.map(async (card) => [card.instanceId, await loadArt(card.cardId)] as const)));
  ctx.fillStyle = "#12141a";
  ctx.fillRect(0, 0, WIDTH, height);
  const glow = ctx.createRadialGradient(WIDTH / 2, 0, 0, WIDTH / 2, 0, 760);
  glow.addColorStop(0, "#25413f");
  glow.addColorStop(1, "rgba(18, 20, 26, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, 760);

  ctx.fillStyle = "#2db89a";
  ctx.font = '700 24px "IBM Plex Mono", monospace';
  ctx.letterSpacing = "6px";
  ctx.fillText("STAGE CLEAR", 80, 88);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = "#f3ead9";
  ctx.font = '700 64px "IBM Plex Sans JP", sans-serif';
  ctx.fillText("プロジェクト完了！", 80, 172);
  ctx.fillStyle = "#aeb5c0";
  ctx.font = '500 30px "IBM Plex Sans JP", sans-serif';
  ctx.fillText(input.stageName, 80, 225);
  ctx.fillText(`最終プロジェクト：${input.finalCaseName}`, 80, 272);

  const statY = 320;
  [
    ["踏破", `${input.clearedNodes} マス`],
    ["最終疲労", `${input.fatigue} / 10`],
    ["デッキ", `${input.deck.length} 枚`],
  ].forEach(([label, value], index) => {
    const x = 80 + index * 310;
    roundedRect(ctx, x, statY, 282, 116, 14);
    ctx.fillStyle = "#1c2028";
    ctx.fill();
    ctx.fillStyle = "#8b909a";
    ctx.font = '600 18px "IBM Plex Sans JP", sans-serif';
    ctx.fillText(label, x + 20, statY + 37);
    ctx.fillStyle = label === "最終疲労" ? "#e86e5b" : "#f3ead9";
    ctx.font = '700 31px "IBM Plex Sans JP", sans-serif';
    ctx.fillText(value, x + 20, statY + 83);
  });

  ctx.fillStyle = "#aeb5c0";
  ctx.font = '600 18px "IBM Plex Mono", monospace';
  ctx.fillText("FINAL DECK", 80, 486);
  input.deck.forEach((card, index) => drawCard(ctx, card, artwork.get(card.instanceId) ?? null, CARD_X[index % 3]!, 520 + Math.floor(index / 3) * 420));

  ctx.fillStyle = "#8b909a";
  ctx.font = '600 18px "IBM Plex Mono", monospace';
  ctx.fillText("ORGANIZATIONS", 80, height - 86);
  ctx.fillStyle = "#f3ead9";
  ctx.font = '600 24px "IBM Plex Sans JP", sans-serif';
  ctx.fillText(input.organizations.join(" ／ ") || "なし", 80, height - 43, WIDTH - 160);
  download(canvas, input.stageName);
}
